import { useLocation, useNavigate } from 'react-router-dom';

import NavigationItem from 'components/NavigationItem';
import AccountIcon from 'components/icons/AccountIcon';
import AirplaneIcon from 'components/icons/AirplaneIcon';
import ApplicationsIcon from 'components/icons/ApplicationsIcon';
import ConnectionsIcon from 'components/icons/ConnectionsIcon';
import DataIcon from 'components/icons/DataIcon';
import HelpIcon from 'components/icons/HelpIcon';
import HomeIcon from 'components/icons/HomeIcon';
import MaintenanceIcon from 'components/icons/MaintenanceIcon';
import ReportsIcon from 'components/icons/ReportsIcon';

import useGlobalStore from 'hooks/useGlobalStore';
import usePageRoutes from 'hooks/usePageRoutes';

import { DRONES } from 'pages/MaintenancePage/mockData';

import useTranslation from './hooks/useTranslation';

import { HELP_PAGE, TEST_ID } from './constants';
import type { FunctionComponent, Props, UserResponse } from './types';

import styles from './styles.module.scss';

const Navigation: FunctionComponent<Props> = ({ className = '', isOpen, testId = TEST_ID }) => {
    const { ACCOUNT, AIR, CHEMICALS, CONNECTIONS, DATA, HELP, HOME, MAINT, REPORTS } =
        useTranslation();
    const {
        airspacePage,
        chemicalsPage,
        connectionsPage,
        dataPage,
        homePage,
        maintenancePage,
        reportsPage,
        userDetailsPage
    } = usePageRoutes();

    const { getValue } = useGlobalStore({ namespace: 'useCurrentUser' });
    const currentUser = getValue('currentUser') as UserResponse | undefined;

    const navigate = useNavigate();
    const { pathname } = useLocation();

    const accountRoute = currentUser?.id ? userDetailsPage(currentUser.id) : undefined;

    const criticalDroneCount = DRONES.filter(drone => drone.status === 'critical').length;

    const navigationItems = [
        { Icon: HomeIcon, alertCount: 0, label: HOME, route: homePage },
        { Icon: DataIcon, alertCount: 0, label: DATA, route: dataPage },
        { Icon: ApplicationsIcon, alertCount: 0, label: CHEMICALS, route: chemicalsPage },
        { Icon: AccountIcon, alertCount: 0, label: ACCOUNT, route: accountRoute },
        { Icon: ReportsIcon, alertCount: 0, label: REPORTS, route: reportsPage },
        { Icon: ConnectionsIcon, alertCount: 0, label: CONNECTIONS, route: connectionsPage },
        {
            Icon: MaintenanceIcon,
            alertCount: criticalDroneCount,
            label: MAINT,
            route: maintenancePage
        },
        { Icon: AirplaneIcon, alertCount: 0, label: AIR, route: airspacePage },
        { Icon: HelpIcon, alertCount: 0, label: HELP, route: HELP_PAGE }
    ];

    const isActiveRoute = (route?: string) =>
        !!route && (pathname === route || pathname.startsWith(route + '/'));

    const handleNavigation = (route?: string) => {
        if (!route) {
            return;
        }

        if (route.startsWith('http')) {
            window.open(route, '_blank', 'noopener,noreferrer');
        } else {
            navigate(route);
        }
    };

    return (
        <nav className={className} data-testid={testId}>
            {navigationItems.map(({ Icon, alertCount, label, route }) => {
                const isActive = isActiveRoute(route);

                const activeIconStyles = isActive ? styles.active : '';

                const iconElement = <Icon className={`${styles.icon} ${activeIconStyles}`} />;

                const iconNode =
                    alertCount > 0 ? (
                        <span className={styles.iconWithAlert}>
                            {iconElement}

                            <span className={styles.alertDot}>{alertCount}</span>
                        </span>
                    ) : (
                        iconElement
                    );

                return (
                    <NavigationItem
                        icon={iconNode}
                        isActive={isActive}
                        isOpen={isOpen}
                        key={label}
                        label={label}
                        onClick={() => handleNavigation(route)}
                    />
                );
            })}
        </nav>
    );
};

export default Navigation;
